import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Briefcase, Bookmark, Sparkles, Building2, ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import JobCard from "@/components/JobCard";
import InternalAd from "@/components/InternalAd";

function StatCard({ icon: Icon, label, value, accent, to }) {
  const inner = (
    <>
      <div className={`h-9 w-9 rounded-md flex items-center justify-center mb-3 ${accent ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-3xl font-black tracking-tight">{value}</div>
      <div className="eyebrow mt-1">{label}</div>
    </>
  );
  const testId = `stat-${label.toLowerCase().replace(/\s+/g, '-')}`;
  if (!to) {
    return <div className="vital-card-static p-5" data-testid={testId}>{inner}</div>;
  }
  return (
    <Link
      to={to}
      className="vital-card-static p-5 block cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2"
      data-testid={testId}
    >
      {inner}
    </Link>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [recommended, setRecommended] = useState([]);
  const [noResume, setNoResume] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanSlotLabel, setScanSlotLabel] = useState(null);
  // "Sticky" flag — once we've shown the waiting line at least once, keep it
  // up for a minimum 800ms so fast (<500ms) scans don't blink past the user.
  const [stickyWait, setStickyWait] = useState(false);
  const scanStartRef = React.useRef(null);
  // Single source of truth for "when may stickyWait be cleared". Any code path
  // (poll else-branch, useEffect flag-path, StrictMode double-invoke) sets this
  // once when the wait starts and both paths refuse to clear before it. Fixes
  // the race where StrictMode's second fetchRecs saw scanStartRef=null and
  // cleared immediately, capping the visible window at ~160ms.
  const minHoldUntilRef = React.useRef(0);
  const [q, setQ] = useState("");

  const scheduleClear = React.useCallback(() => {
    const remaining = Math.max(0, minHoldUntilRef.current - Date.now());
    if (remaining > 0) {
      setTimeout(() => setStickyWait(false), remaining);
    } else {
      setStickyWait(false);
    }
  }, []);

  const fetchRecs = React.useCallback(async () => {
    try {
      const r = await api.get("/jobs/recommended");
      const isScanning = !!r.data.scanning;
      setRecommended(r.data.items || []);
      setNoResume(!!r.data.no_resume);
      setScanSlotLabel(r.data?.scan?.slot_label || null);
      if (isScanning) {
        if (scanStartRef.current == null) scanStartRef.current = Date.now();
        // Extend the min-hold window on every fresh "scanning:true" observation.
        if (minHoldUntilRef.current < Date.now() + 300) {
          minHoldUntilRef.current = Date.now() + 800;
        }
        setScanning(true);
        setStickyWait(true);
      } else {
        setScanning(false);
        scheduleClear();
        scanStartRef.current = null;
      }
      return isScanning;
    } catch {
      setRecommended([]);
      setScanning(false);
      scheduleClear();
      return false;
    }
  }, [scheduleClear]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    // If the user just uploaded a resume on /profile, honour the min-800ms hold
    // even before the first /jobs/recommended poll returns — the scan often
    // finishes in <500ms which is faster than a page navigation.
    // Use sessionStorage as the "consumed" flag so React StrictMode's double-
    // mount in dev doesn't reset the window when Effect2 sees an empty flag.
    try {
      const ts = Number(localStorage.getItem("karm_scan_started_at") || 0);
      if (ts && Date.now() - ts < 30_000) {
        scanStartRef.current = ts;
        // Guarantee the wait line stays visible for at least 500ms of on-screen
        // time (min-hold ≥ 500ms so React StrictMode double-mount + a fast
        // fetchRecs race cannot cap the window).
        const holdUntil = Date.now() + Math.max(500, 800 - (Date.now() - ts));
        if (minHoldUntilRef.current < holdUntil) minHoldUntilRef.current = holdUntil;
        setStickyWait(true);
        localStorage.removeItem("karm_scan_started_at");
      }
    } catch { /* noop */ }
    fetchRecs();
  }, [fetchRecs]);

  // Poll every 3s while a scan is running. Stops the moment the endpoint
  // stops returning `scanning: true`.
  useEffect(() => {
    if (!scanning) return undefined;
    const t = setInterval(() => { fetchRecs(); }, 3000);
    return () => clearInterval(t);
  }, [scanning, fetchRecs]);

  const isRecruiter = user?.role === "recruiter";
  const isAdmin = user?.role === "admin";
  // Search scope changes per persona
  const scope = isAdmin
    ? { placeholder: "Search jobs, seekers, recruiters, companies…", target: "jobs" }
    : isRecruiter
    ? { placeholder: "Search seekers, your postings, skills, locations…", target: "admin" }
    : { placeholder: "Search jobs — title, skill, company, city…", target: "jobs" };

  const onSearch = (e) => {
    e.preventDefault();
    if (isRecruiter) {
      navigate(`/admin${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    } else {
      navigate(`/jobs${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    }
  };

  return (
    <div className="container py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow mb-1">Dashboard</p>
          <h1 className="display-2">Welcome back, {user?.name?.split(" ")[0] || "there"}.</h1>
        </div>
      </div>

      {/* Long persona-aware search bar */}
      <form
        onSubmit={onSearch}
        className="vital-card-static search-shadow rounded-2xl p-2 flex items-center gap-2 mb-10"
        data-testid="dashboard-search-form"
      >
        <div className="flex-1 flex items-center gap-3 px-4">
          <Search className="h-5 w-5 text-muted-foreground shrink-0" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={scope.placeholder}
            className="border-0 focus-visible:ring-0 shadow-none text-base h-12 px-0"
            data-testid="dashboard-search-input"
          />
        </div>
        <Button type="submit" size="lg" className="h-12 px-7 rounded-xl" data-testid="dashboard-search-btn">
          Search
        </Button>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard icon={Briefcase} label="Applications" value={stats.applications ?? 0} accent to="/applications" />
        <StatCard icon={Bookmark} label="Saved Jobs" value={stats.saved_jobs ?? 0} to="/saved" />
        {isAdmin && <StatCard icon={Sparkles} label="Posts created" value={stats.content_posts ?? 0} to="/content-studio" />}
        <StatCard icon={Building2} label="Companies" value={stats.companies ?? 0} to="/companies" />
      </div>

      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="eyebrow mb-1">Tailored to you</p>
          <h2 className="display-2 text-2xl md:text-3xl">Recommended roles</h2>
        </div>
        <Button asChild variant="ghost" data-testid="dashboard-see-all">
          <Link to="/jobs">Browse all <ArrowRight className="ml-1 h-4 w-4" /></Link>
        </Button>
      </div>
      {stickyWait && (
        <div
          className="text-sm text-muted-foreground italic mb-4 flex items-center gap-2"
          data-testid="scan-waiting-line"
          role="status"
          aria-live="polite"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
          Please wait while we find relevant jobs for you{scanSlotLabel ? ` — scoring against your ${scanSlotLabel} resume` : ""}…
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {recommended.map((j) => <JobCard key={j.job_id} job={j} recommended />)}
      </div>

      {noResume && !stickyWait && (
        <div
          className="vital-card-static p-8 text-center flex flex-col items-center gap-3"
          data-testid="recommendations-no-resume"
        >
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <h3 className="text-lg font-bold">Upload a resume to unlock recommendations</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            KARM matches roles against your resume — your current and past titles, skills and level.
            Add a resume to any of your 5 slots and we'll surface equivalent and better GCC roles here.
          </p>
          <Button asChild size="lg" data-testid="no-resume-upload-cta">
            <Link to="/profile">Upload your resume <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
      )}

      <div className="mt-10">
        <InternalAd slot="dashboard_sidebar" />
      </div>
    </div>
  );
}
